import React from 'react';
import { connect, useDispatch } from 'react-redux';
import { Button, LinearProgress } from '@mui/material';
import {
  ChangeCircle,
  Publish,
  PublishedWithChanges,
  Unpublished,
  Warning,
} from '@mui/icons-material';
import StagingDataTable from './StagingDataTable';
import { selectStagingEquipment } from '../../../redux/selectors/staging';
import BuildingStagingLoader from '../../loaders/BuildingStagingLoader';
import { Page, Row } from '../../util/Page';
import { promoteEquipsTopicsAction, updateStaging } from '../../../redux/actions/staging';
import ColumnDefinition from '../constants/ColumnDefinition';
import TableAction from '../constants/TableAction';
import { dateTime } from '../../../utils/parsing';
import { fetchBuildingDbo } from '../../../redux/actions/buildings';
import StatusBadge from '../constants/StatusBadge';
import PromotionDialog from './dialogs/PromotionDialog';
import { getEnv } from '../../../api/urls';
import { SET_SELECTED_EQUIPMENT } from '../../../redux/actions/actiontypes';

const EquipmentDataTable = ({
  loaded,
  buildingId,
  filteredEquipment,
  equipTypes,
  equipSubTypes,
  promoteEquipment,
  updateStagingUncaught,
  equipmentValidation,
  pointsValidation,
  pointsByEquipId,
  onPromotionResults,
  fetchBuildingDboUncaught,
  selectedEquipment,
}) => {
  if (!loaded) {
    return (
      <Page>
        <Row>
          <BuildingStagingLoader key={`stg-${buildingId}`} buildingId={buildingId} />
          <LinearProgress color="secondary" className="loading" />
        </Row>
      </Page>
    );
  }

  const dispatch = useDispatch();
  // Never include SKIP equipment for equipment management since it's a completely ephemeral equipment that should not be managed or promoted
  const skiplessEquipment = filteredEquipment.filter((e) => e.equip_id !== '__SKIP__');
  const [loading, setLoading] = React.useState(false);
  const [promotionOpen, setPromotionOpen] = React.useState(false);

  if (skiplessEquipment.length <= 0) {
    return (
      <Page>
        <Row>There are no results to show.</Row>
      </Page>
    );
  }

  const columns = [
    new ColumnDefinition({
      id: 'status',
      label: 'Status',
      type: 'badges',
      sortable: true,
      render: (equip) => {
        const statuses = [];
        if (!equip.last_promoted)
          statuses.push(
            new StatusBadge({
              id: 'promoted',
              icon: Unpublished,
              message: 'This equipment is not promoted.',
            })
          );
        else
          statuses.push(
            new StatusBadge({
              id: 'unpromoted',
              icon: PublishedWithChanges,
              color: 'success',
              message: `This equipment was last promoted ${dateTime(equip.last_promoted)}.`,
            })
          );
        let validationErrors = '';
        if (equipmentValidation?.[equip?.equip_id]?.length > 0) {
          // eslint-disable-next-line prefer-template
          validationErrors = `${
            validationErrors ? `${validationErrors}\n` : ''
          }This equipment contains validation errors:${[
            '',
            ...equipmentValidation[equip.equip_id],
          ].join('\n  -> ')}`;
        }
        const equipmentPointsValidation =
          pointsByEquipId && equip && equip.equip_id && pointsValidation
            ? Object.values(pointsByEquipId[equip.equip_id] || {})
                .filter((p) => pointsValidation[p.topic]?.length > 0)
                .reduce((res, p) => {
                  res[p.topic] = pointsValidation[p.topic];
                  return res;
                }, {})
            : {};
        if (Object.keys(equipmentPointsValidation)?.length > 0) {
          // eslint-disable-next-line prefer-template
          const trimmedInvalidPoints = Object.values(equipmentPointsValidation).slice(0, 5);
          const isTrimmed =
            trimmedInvalidPoints.length !== Object.values(equipmentPointsValidation).length;
          validationErrors = `${
            validationErrors ? `${validationErrors}\n` : ''
          }This equipment contains point validation errors:${['', ...trimmedInvalidPoints].join(
            '\n  -> '
          )} ${isTrimmed ? '\n\nAdditional point validation errors unlisted' : ''}`;
        }
        if (validationErrors)
          statuses.push(
            new StatusBadge({
              id: 'promotion_error',
              icon: Warning,
              color: 'error',
              message: validationErrors,
            })
          );
        if (equip.last_promoted && equip.last_promoted < equip.modified)
          statuses.push(
            new StatusBadge({
              id: 'changes',
              icon: ChangeCircle,
              color: 'warning',
              message: `This equipment has unpromoted changes.`,
            })
          );
        return statuses;
      },
    }),
    new ColumnDefinition({
      id: 'equip_type',
      label: 'Equipment Type',
      type: 'button',
      action: 'change_equip_type',
      sortable: true,
      render: (equip) =>
        equipSubTypes[equip.equipment_subtype_id]?.tagName ||
        equipTypes[equip.equipment_type_id]?.tagName,
    }),
    new ColumnDefinition({
      id: 'confidence',
      label: 'Confidence',
      type: 'linear',
      sortable: true,
      sortFunction: (equipA, equipB) => {
        const valueA = equipA.confidences.equip_type || 0;
        const valueB = equipB.confidences.equip_type || 0;
        if (valueA < valueB) return -1;
        if (valueA > valueB) return 1;
        return 0;
      },
      render: (equip) => equip.confidences.equip_type,
    }),
    new ColumnDefinition({
      id: 'equip_id',
      label: 'Equip ID',
      type: 'text',
      render: (equip) => equip.equip_id,
    }),
    new ColumnDefinition({
      id: 'suffix',
      label: 'Suffix',
      type: 'text',
      render: (equip) => equip.data.suffix,
    }),
    new ColumnDefinition({
      id: 'device_id',
      label: 'Device ID',
      type: 'text',
      sortable: true,
      render: (equip) => equip.data.device_id,
    }),
    new ColumnDefinition({
      id: 'object_name',
      label: 'Object Name',
      type: 'text',
      render: (equip) => equip.data.objectName,
    }),
    new ColumnDefinition({
      id: 'model_name',
      label: 'Model Name',
      type: 'text',
      render: (equip) => equip.data.modelName,
    }),
  ];

  const update = (data, newEquipValues) => {
    const equipIds = new Set(data.map((datum) => datum.equip_id));
    const updateRowValues = data.map((datum) => ({
      ...newEquipValues,
      'e.equip_id': datum.equip_id,
    }));
    setLoading(true);
    return updateStagingUncaught([...equipIds], updateRowValues).then(() => {
      setLoading(false);
    });
  };

  const onPromotionDialogClosed = (result) => {
    onPromotionResults(result);
    setPromotionOpen(false);
  };

  const actions = [
    new TableAction({
      id: 'change_equip_type',
      label: 'Change Equip Type',
      doSave: update,
    }),
  ];

  return (
    <>
      <PromotionDialog
        open={promotionOpen}
        selected={selectedEquipment}
        loading={loading}
        pointsByEquipId={pointsByEquipId}
        promoteEquipment={promoteEquipment}
        onClose={onPromotionDialogClosed}
      />
      <StagingDataTable
        title="Equipment"
        columns={columns}
        data={skiplessEquipment}
        selected={selectedEquipment}
        onItemSelected={(newEquipment) =>
          dispatch({ type: SET_SELECTED_EQUIPMENT, equipment: newEquipment })
        }
        rowIdKey="equip_id"
        actions={actions}
        loading={loading}
        buildingId={buildingId}
      >
        <Button
          startIcon=<Publish />
          disabled={selectedEquipment.length === 0}
          onClick={() => {
            setPromotionOpen(true);
          }}
          sx={{ display: 'flex', marginLeft: 'auto' }}
        >
          Promote {selectedEquipment.length}
        </Button>
      </StagingDataTable>
      {getEnv() === 'GCP' ? (
        <Button
          onClick={() => {
            fetchBuildingDboUncaught();
          }}
          sx={{ display: 'flex', marginLeft: 'auto' }}
        >
          Building Config
        </Button>
      ) : null}
    </>
  );
};

const mapStateToProps = (state, { buildingId }) => {
  // Including filteredEquipment
  const staging = selectStagingEquipment(state, buildingId);
  const {
    entities: {
      staging: {
        pointsByEquipId,
        validation: { equipment, points },
        selectedEquipment,
      },
    },
  } = state;
  const equipmentValidation = equipment[buildingId ?? ''] ?? {};
  const pointsValidation = points[buildingId ?? ''] ?? {};
  return {
    ...staging,
    buildingId,
    equipmentValidation,
    pointsValidation,
    equipTypes: state.entities.equipTypes.byId,
    equipSubTypes: state.entities.equipSubTypes.byId,
    pointsByEquipId: pointsByEquipId[buildingId ?? ''] ?? {},
    selectedEquipment,
  };
};

const mapDispatchToProps = (dispatch, { buildingId }) => ({
  promoteEquipment: (equipmentList) =>
    dispatch(promoteEquipsTopicsAction(buildingId, equipmentList, [], [])),
  updateStagingUncaught: (equipId, data) => dispatch(updateStaging(buildingId, equipId, data)),
  fetchBuildingDboUncaught: () => dispatch(fetchBuildingDbo(buildingId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EquipmentDataTable);
